// Generic Embla initializer
// - Finds [data-embla] roots
// - Reads options from data-embla-options (JSON)
// - Optionally reads data-embla-breakpoints (JSON mapping of media queries to options)
// - Binds prev/next buttons and dots
// - Provides simple autoplay (pauses on hover/focus, respects reduced motion)

(function () {
  function parseJsonAttr(el, name) {
    const raw = el.getAttribute(name);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      console.warn(`embla-init: Invalid JSON in ${name}`, e);
      return null;
    }
  }

  function merge(a, b) {
    return Object.assign({}, a || {}, b || {});
  }

  function computeResponsiveOptions(root, baseOptions) {
    const breakpoints = parseJsonAttr(root, 'data-embla-breakpoints');
    if (!breakpoints) return baseOptions || {};
    let opts = Object.assign({}, baseOptions || {});
    Object.keys(breakpoints).forEach((query) => {
      try {
        if (window.matchMedia(query).matches) {
          opts = merge(opts, breakpoints[query]);
        }
      } catch (e) {
        console.warn('embla-init: Bad media query in data-embla-breakpoints', query);
      }
    });
    return opts;
  }

  function setupDots(root, embla) {
    const dotsRoot = root.querySelector('[data-embla-dots]');
    if (!dotsRoot) return { destroy: function () {} };
    dotsRoot.innerHTML = '';
    const snaps = embla.scrollSnapList();
    const dots = snaps.map(function (_snap, index) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'embla__dot';
      btn.setAttribute('aria-label', `Go to slide ${index + 1}`);
      btn.addEventListener('click', function () { embla.scrollTo(index); });
      dotsRoot.appendChild(btn);
      return btn;
    });
    function setActiveDot() {
      const selected = embla.selectedScrollSnap();
      dots.forEach(function (d, i) {
        if (i === selected) {
          d.classList.add('is-selected');
          d.setAttribute('aria-current', 'true');
        } else {
          d.classList.remove('is-selected');
          d.removeAttribute('aria-current');
        }
      });
    }
    embla.on('select', setActiveDot);
    setActiveDot();
    return {
      destroy: function () {
        embla.off('select', setActiveDot);
      },
    };
  }

  function syncNavButton(button, isEnabled, immediate) {
    if (!button) return;

    button.setAttribute('aria-disabled', isEnabled ? 'false' : 'true');
    button.disabled = !isEnabled;

    // Keep buttons always visible, even when disabled
    button.classList.remove('tw-opacity-0', 'tw-pointer-events-none');
  }

  window.SBEmblaNav = window.SBEmblaNav || {};
  window.SBEmblaNav.set = syncNavButton;

  function setupPrevNext(root, embla) {
    const prev = root.querySelector('[data-embla-prev]');
    const next = root.querySelector('[data-embla-next]');
    if (!prev && !next) {
      return { destroy: function () {} };
    }

    const handlePrev = function () { embla.scrollPrev(); };
    const handleNext = function () { embla.scrollNext(); };

    const applyState = function (immediate) {
      const helper = window.SBEmblaNav && typeof window.SBEmblaNav.set === 'function';
      const canPrev = embla.canScrollPrev();
      const canNext = embla.canScrollNext();
      if (prev) {
        if (helper) {
          window.SBEmblaNav.set(prev, canPrev, immediate);
        } else {
          prev.disabled = !canPrev;
        }
      }
      if (next) {
        if (helper) {
          window.SBEmblaNav.set(next, canNext, immediate);
        } else {
          next.disabled = !canNext;
        }
      }
    };

    const handleSelect = function () { applyState(false); };
    const handleReInit = function () { applyState(true); };

    if (prev) prev.addEventListener('click', handlePrev);
    if (next) next.addEventListener('click', handleNext);

    embla.on('select', handleSelect);
    embla.on('reInit', handleReInit);
    applyState(true);
    return {
      destroy: function () {
        embla.off('select', handleSelect);
        embla.off('reInit', handleReInit);
        if (prev) prev.removeEventListener('click', handlePrev);
        if (next) next.removeEventListener('click', handleNext);
      },
    };
  }

  function setupAutoplay(root, embla, options) {
    var auto = options && options.autoplay;
    if (!auto) return { destroy: function () {} };
    var delay = (typeof auto === 'object' && auto.delay) ? auto.delay : (typeof auto === 'number' ? auto : 4000);
    var stopOnMouseEnter = (typeof auto === 'object' && auto.stopOnMouseEnter) !== false; // default true
    var stopOnInteraction = (typeof auto === 'object' && auto.stopOnInteraction) !== false; // default true
    var mediaReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaReduce.matches) return { destroy: function () {} };

    var timer = null;
    var paused = false;
    var rafId = null;

    function play() {
      if (paused) return;
      stop();
      timer = window.setTimeout(function () {
        embla.scrollNext();
      }, delay);
    }
    function stop() {
      if (timer) { window.clearTimeout(timer); timer = null; }
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    }
    function pause() { paused = true; stop(); }
    function resume() { paused = false; play(); }

    embla.on('select', play);
    embla.on('pointerDown', function () { if (stopOnInteraction) pause(); });
    if (stopOnMouseEnter) {
      root.addEventListener('mouseenter', pause);
      root.addEventListener('mouseleave', resume);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', resume);
    }
    play();
    return {
      destroy: function () {
        stop();
        embla.off('select', play);
        if (stopOnMouseEnter) {
          root.removeEventListener('mouseenter', pause);
          root.removeEventListener('mouseleave', resume);
          root.removeEventListener('focusin', pause);
          root.removeEventListener('focusout', resume);
        }
      },
    };
  }

  function setupA11y(root, embla) {
    var viewport = root.querySelector('.embla__viewport');
    if (viewport) {
      if (!viewport.hasAttribute('aria-roledescription')) viewport.setAttribute('aria-roledescription', 'carousel');
      viewport.setAttribute('aria-live', 'polite');
    }
    var live = root.querySelector('[data-embla-live]');
    if (!live) return { destroy: function () {} };
    function announce() {
      var idx = embla.selectedScrollSnap() + 1;
      live.textContent = 'Slide ' + idx + ' of ' + embla.scrollSnapList().length;
    }
    embla.on('select', announce);
    announce();
    return {
      destroy: function () { embla.off('select', announce); },
    };
  }

  function initRoot(root) {
    if (root.__emblaInitialized) return;
    var viewport = root.querySelector('.embla__viewport');
    var container = viewport && root.querySelector('.embla__container');
    if (!viewport || !container) return;

    var baseOptions = parseJsonAttr(root, 'data-embla-options') || {};
    var options = computeResponsiveOptions(root, baseOptions);

    var embla = window.EmblaCarousel(viewport, options);
    root.__emblaInstance = embla;
    try {
      var initEvent = new CustomEvent('sb:embla:init', { detail: { root: root, embla: embla } });
      root.dispatchEvent(initEvent);
    } catch (_) {}
    var teardown = [];
    teardown.push(setupPrevNext(root, embla));
    teardown.push(setupDots(root, embla));
    teardown.push(setupAutoplay(root, embla, options));
    teardown.push(setupA11y(root, embla));

    root.__emblaInitialized = true;
    root.__emblaDestroy = function () {
      teardown.forEach(function (t) { try { t.destroy(); } catch (_) {} });
      try {
        var destroyEvent = new CustomEvent('sb:embla:destroy', { detail: { root: root, embla: embla } });
        root.dispatchEvent(destroyEvent);
      } catch (_) {}
      root.__emblaInstance = null;
      try { delete root.__emblaInstance; } catch (_) {}
      try { embla.destroy(); } catch (_) {}
      root.__emblaInitialized = false;
    };
  }

  function initAll() {
    if (!window.EmblaCarousel) return;
    document.querySelectorAll('[data-embla]').forEach(initRoot);
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    initAll();
    // Observe dynamic inserts
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'childList') {
          initAll();
        }
      }
    });
    try { mo.observe(document.documentElement || document.body, { childList: true, subtree: true }); } catch (_) {}

    // If Embla library loads late
    if (!window.EmblaCarousel) {
      window.addEventListener('load', initAll, { once: true });
    }
  });
})();
